import React, { useEffect, useState } from "react";
import { Button, Form, Table, Row, Col, Container, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";

const Partnership = () => {
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [selectedTaluka, setSelectedTaluka] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [warning, setWarning] = useState("");

  const safeArray = (json) =>
    Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];

  /* ==========================
     Checkbox Dropdown
  ========================== */
  const CheckboxDropdown = ({
    label,
    options,
    idKey,
    nameKey,
    selected,
    setSelected,
    disabled,
    dropdownKey,
  }) => {
    const toggle = () => {
      if (!disabled) {
        setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
      }
    };

    return (
      <Form.Group className="mb-3 position-relative">
        <Form.Label>{label}</Form.Label>

        <div
          className={`form-select ${disabled ? "disabled" : ""}`}
          onClick={toggle}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        >
          {selected.length === 0
            ? "All"
            : selected.length === options.length
            ? "All Selected"
            : `${selected.length} Selected`}
        </div>

        {openDropdown === dropdownKey && !disabled && (
          <div
            className="position-absolute bg-white border rounded shadow-sm w-100"
            style={{
              zIndex: 9999,
              maxHeight: 220,
              overflowY: "auto",
              bottom: "100%",
              marginBottom: 8,
            }}
          >
            <div className="px-3 py-2 border-bottom fw-bold">
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={options.length && selected.length === options.length}
                onChange={(e) =>
                  setSelected(e.target.checked ? options.map((o) => o[idKey]) : [])
                }
              />
            </div>

            {options.map((o) => (
              <div key={o[idKey]} className="px-3 py-2">
                <Form.Check
                  type="checkbox"
                  label={o[nameKey]}
                  checked={selected.includes(o[idKey])}
                  onChange={() => {
                    if (
                      dropdownKey === "taluka" &&
                      !selected.includes(o[idKey]) &&
                      selected.length >= 3
                    ) {
                      setWarning("Please select a maximum of 3 Talukas");
                      return;
                    }
                    setWarning("");
                    setSelected(
                      selected.includes(o[idKey])
                        ? selected.filter((x) => x !== o[idKey])
                        : [...selected, o[idKey]]
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    );
  };

  /* ==========================
     0️⃣ Initial Load – FULL DATA
  ========================== */
  useEffect(() => {
    if (!apiBaseUrl) return;

    fetch(`${apiBaseUrl}/partnership/report`)
      .then((r) => r.json())
      .then((json) => setTableData(safeArray(json)))
      .catch(() => setTableData([]));
  }, [apiBaseUrl]);

  /* ==========================
     1️⃣ Load Districts
  ========================== */
  useEffect(() => {
    if (!apiBaseUrl) return;

    fetch(`${apiBaseUrl}/partnership/districts`)
      .then((r) => r.json())
      .then((json) => setDistrictList(safeArray(json)))
      .catch(() => setDistrictList([]));
  }, [apiBaseUrl]);

  /* ==========================
     2️⃣ District → Taluka
  ========================== */
  useEffect(() => {
    if (!apiBaseUrl || !selectedDistrict.length) {
      setTalukaList([]);
      setSelectedTaluka([]);
      return;
    }

    fetch(
      `${apiBaseUrl}/partnership/districts/${selectedDistrict.join(",")}/talukas`
    )
      .then((r) => r.json())
      .then((json) => {
        setTalukaList(safeArray(json));
        setSelectedTaluka([]);
      })
      .catch(() => setTalukaList([]));
  }, [selectedDistrict, apiBaseUrl]);

  /* ==========================
     3️⃣ Filtered Report
  ========================== */
  useEffect(() => {
    if (!apiBaseUrl) return;

    if (!selectedDistrict.length && !selectedTaluka.length) return;

    let url = `${apiBaseUrl}/partnership/report`;
    const params = [];

    if (selectedDistrict.length)
      params.push(`districtIds=${selectedDistrict.join(",")}`);
    if (selectedTaluka.length)
      params.push(`talukaIds=${selectedTaluka.join(",")}`);

    if (params.length) url += `?${params.join("&")}`;

    fetch(url)
      .then((r) => r.json())
      .then((json) => setTableData(safeArray(json)))
      .catch(() => setTableData([]));
  }, [selectedDistrict, selectedTaluka, apiBaseUrl]);

  /* ==========================
     Search + Totals
  ========================== */
  const filteredView = tableData.filter((r) =>
    Object.values(r)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalBeneficiaries = filteredView.reduce(
    (t, r) => t + Number(r.labharshi_sankhya || 0),
    0
  );

  /* ==========================
     Excel Export
  ========================== */
  const exportExcel = () => {
    const rows = filteredView.map((r, i) => ({
      "अ.क्र": i + 1,
      जिल्हा: r.district_name,
      तालुका: r.taluka_name,
      "संस्थेचे नाव": r.sanstha,
      योजना: r.yojana,
      "लाभार्थी संख्या": r.labharshi_sankhya,
      रक्कम: r.amount,
      "लाभाचे स्वरूप": r.labhache_sorup,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Partnership");
    XLSX.writeFile(wb, "Partnership_Report.xlsx");
  };

  return (
    <Container fluid>
      <Row>
        <Col md={2} className="p-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/")}
            className="mb-3 w-100"
          >
            Back
          </Button>

          <div className="p-3 rounded shadow-sm" style={{ background: "#EAF0F9" }}>
            <div className="d-flex justify-content-between mb-2">
              <h5>Filters</h5>
              <FaSyncAlt
                style={{ cursor: "pointer" }}
                onClick={() => window.location.reload()}
              />
            </div>

            <CheckboxDropdown
              label="जिल्हा"
              options={districtList}
              idKey="district_id"
              nameKey="district"
              selected={selectedDistrict}
              setSelected={setSelectedDistrict}
              disabled={!districtList.length}
              dropdownKey="district"
            />

            <CheckboxDropdown
              label="तालुका"
              options={talukaList}
              idKey="taluka_id"
              nameKey="taluka_name"
              selected={selectedTaluka}
              setSelected={setSelectedTaluka}
              disabled={!talukaList.length}
              dropdownKey="taluka"
            />
          </div>

          {warning && <Alert variant="warning">{warning}</Alert>}
        </Col>

        <Col md={10} className="p-4">
          <div className="d-flex justify-content-between mb-3">
            <Form.Control
              placeholder="Search"
              style={{ width: 250 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button onClick={exportExcel}>Export Excel</Button>
          </div>

          <Table bordered striped hover>
            <thead>
              <tr>
                <th>अ.क्र</th>
                <th>जिल्हा</th>
                <th>तालुका</th>
                <th>संस्थेचे नाव</th>
                <th>योजना</th>
                <th>लाभार्थी संख्या</th>
                <th>रक्कम</th>
                <th>लाभाचे स्वरूप</th>
              </tr>
            </thead>

            <tbody>
              <tr className="fw-bold">
                <td colSpan={5}>Total</td>
                <td>{totalBeneficiaries}</td>
                <td></td>
                <td></td>
              </tr>

              {filteredView.map((r, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{r.district_name}</td>
                  <td>{r.taluka_name}</td>
                  <td>{r.sanstha}</td>
                  <td>{r.yojana}</td>
                  <td>{r.labharshi_sankhya}</td>
                  <td>{r.amount}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>
                    {r.labhache_sorup}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default Partnership;
